import { useCallback, useLayoutEffect, useRef } from 'react';

/**
 * Preserves the scroll position when content is prepended to the top of a
 * scroll container (infinite-scroll upward). Call `beforePrepend()` just before
 * the new items are inserted into state, and the hook will restore the position
 * on the next layout paint so the viewport doesn't jump.
 *
 * Only corrects top-prepends. Bottom-appends (streaming) are unaffected — the
 * host's auto-scroll-to-bottom logic continues to handle those.
 */
export function useScrollAnchor<T extends HTMLElement = HTMLDivElement>() {
	const containerRef = useRef<T>(null);
	const savedScrollHeightRef = useRef<number>(0);
	const isPrependingRef = useRef(false);

	// Called synchronously before dispatching the prepend action.
	const beforePrepend = useCallback(() => {
		if (containerRef.current) {
			savedScrollHeightRef.current = containerRef.current.scrollHeight;
			isPrependingRef.current = true;
		}
	}, []);

	// useLayoutEffect fires before the browser paints, so the scroll adjustment
	// is invisible to the user — no flicker.
	useLayoutEffect(() => {
		if (!isPrependingRef.current || !containerRef.current) return;
		isPrependingRef.current = false;
		const delta = containerRef.current.scrollHeight - savedScrollHeightRef.current;
		if (delta > 0) {
			containerRef.current.scrollTop += delta;
		}
	});

	return { containerRef, beforePrepend };
}
